import BoltIcon from "@mui/icons-material/Bolt"
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded"
import PriorityHighIcon from "@mui/icons-material/PriorityHigh"
import RuleRoundedIcon from "@mui/icons-material/RuleRounded"
import {
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
} from "@mui/material"
import { ReactElement, useState } from "react"
import { useNavigate } from "react-router-dom"

import DashboardPanelFrame from "../../components/dashboard/dashboardPanelFrame"
import ListDevices from "../../components/dashboard/listDevices"
import SingleKpiCard from "../../components/dashboard/singleKpiCard"
import ErrorDisplay from "../../components/fallback/errorDisplay"
import {
    EVChargerIcon,
    ElectricBoilerIcon,
    HeatPumpIcon,
} from "../../components/icons/icons"
import DashboardHomepageSkeleton from "../../components/loading/skeletons/dashboardHomepageSkeleton"
import ReleaseSnackbar from "../../components/snackbar/releaseSnackbar"
import {
    AppCard,
    AppCardContent,
    AppCardHeader,
} from "../../components/surfaces/appCard"
import AppPaper from "../../components/surfaces/appPaper"
import { DISPLAY_ROLES, LCT_MANU_APPROVED } from "../../constants/roles"
import {
    TT_DEVICES_ALERT,
    TT_DEVICES_REQUIRING_ATTENTION,
} from "../../constants/tooltips"
import { useGetUserData } from "../../hooks/useGetUserData"
import {
    AWAITING_ASSESSMENT_DEVICES,
    DRAFT_EB_DEVICES,
    DRAFT_EVAC_DEVICES,
    DRAFT_EVDC_DEVICES,
    DRAFT_HP_DEVICES,
    MANU_DRAFT_DEVICES,
    NON_COMPLIANT_DEVICES,
} from "../../router/routes"
import { useGetManufacturerHomepageData } from "../../services/middleware/hooks/manufacturerInterface/useGetManufacturerHomepageData"
import { GRID_SPACING } from "../../themes/defaultThemeParams"

/**
 * Renders the dashboard for Manufacturer users which is displayed as their homepage
 *
 */
const ManufacturerHomepage = (): ReactElement => {
    const { firstName } = useGetUserData()
    const { data, error, isLoading } = useGetManufacturerHomepageData()
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)

    if (isLoading) {
        return <DashboardHomepageSkeleton />
    }
    if (error) {
        return <ErrorDisplay error={error} />
    }
    if (!data) {
        return <Typography> KPI Currently Unavailable</Typography>
    }

    return (
        <Stack
            direction={{ xs: "column", md: "column" }}
            spacing={GRID_SPACING}
            alignItems="stretch"
        >
            <Stack>
                <DashboardPanelFrame
                    userName={firstName}
                    permission={DISPLAY_ROLES[LCT_MANU_APPROVED]}
                />
            </Stack>

            <Stack
                direction={{ xs: "column", md: "row" }}
                gap={GRID_SPACING}
                justifyContent="center"
                marginBottom={GRID_SPACING}
            >
                <SingleKpiCard
                    Icon={HourglassTopRoundedIcon}
                    value={data.totDevicesAwaitingAssessment}
                    title="Devices Awaiting Assessment"
                    link={AWAITING_ASSESSMENT_DEVICES}
                />
                <SingleKpiCard
                    Icon={RuleRoundedIcon}
                    value={data.totDevicesNonCompliant}
                    title="Non-Compliant Devices"
                    link={NON_COMPLIANT_DEVICES}
                />
                <SingleKpiCard
                    Icon={PriorityHighIcon}
                    value={data.totLiveAlerts}
                    title="Devices with Alerts"
                />
            </Stack>

            <Stack
                direction={{ xs: "column", md: "row" }}
                gap={GRID_SPACING}
                justifyContent="center"
                marginBottom={GRID_SPACING}
            >
                <ListDevices
                    cardTitle="Latest Devices Requiring Attention"
                    cardDescription="This is a list of the latest 20 devices (both demand and generation) that require your attention"
                    cardTooltip={TT_DEVICES_REQUIRING_ATTENTION}
                    data={data.topNonCompliantSummary}
                />
                <ListDevices
                    cardTitle="My Devices with Alerts"
                    cardDescription="The following devices have had an alert raised against them. Click on each to view more details"
                    cardTooltip={TT_DEVICES_ALERT}
                    data={data.topLiveAlerts.map((alert) => ({
                        ...alert,
                        systemReference: alert.deviceSystemRef,
                    }))}
                />
            </Stack>

            <Stack
                direction={{ xs: "column", md: "row" }}
                gap={GRID_SPACING}
                justifyContent="center"
                marginBottom={GRID_SPACING}
            >
                <ListDevices
                    cardTitle="Recently Approved Devices"
                    cardDescription="This is a list of the latest 20 devices (both demand and generation) that have been approved"
                    cardTooltip={TT_DEVICES_REQUIRING_ATTENTION}
                    data={data.topApprovedSummary}
                />
                <AppPaper sx={{ width: { xs: "100%", md: "100%", lg: "50%" } }}>
                    <AppCard sx={{ padding: 0 }}>
                        <AppCardHeader
                            title="Draft Devices"
                            subheader="Draft devices are ones that have not yet been completed and submitted for assessment"
                            titleTypographyProps={{
                                variant: "h5",
                                fontWeight: "500",
                            }}
                        />
                        <AppCardContent>
                            <List
                                sx={{
                                    p: 0,
                                    width: "100%",
                                    backgroundColor: "background.paper",
                                }}
                            >
                                <ListItem>
                                    <ListItemButton
                                        onClick={() =>
                                            navigate(DRAFT_EVAC_DEVICES)
                                        }
                                    >
                                        <ListItemIcon>
                                            <EVChargerIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primaryTypographyProps={{
                                                variant: "h6",
                                            }}
                                            primary={
                                                <strong>
                                                    {data.totEVACDrafts}
                                                </strong>
                                            }
                                            secondary="Draft EVAC Applications"
                                        />
                                    </ListItemButton>
                                </ListItem>
                                <Divider variant="middle" component="li" />

                                <ListItem>
                                    <ListItemButton
                                        onClick={() =>
                                            navigate(DRAFT_EVDC_DEVICES)
                                        }
                                    >
                                        <ListItemIcon>
                                            <EVChargerIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primaryTypographyProps={{
                                                variant: "h6",
                                            }}
                                            primary={
                                                <strong>
                                                    {data.totEVDCDrafts}
                                                </strong>
                                            }
                                            secondary="Draft EVDC Applications"
                                        />
                                    </ListItemButton>
                                </ListItem>
                                <Divider variant="middle" component="li" />

                                <ListItem>
                                    <ListItemButton
                                        onClick={() =>
                                            navigate(DRAFT_HP_DEVICES)
                                        }
                                    >
                                        <ListItemIcon>
                                            <HeatPumpIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primaryTypographyProps={{
                                                variant: "h6",
                                            }}
                                            primary={
                                                <strong>
                                                    {data.totHPDrafts}
                                                </strong>
                                            }
                                            secondary="Draft HP Applications"
                                        />
                                    </ListItemButton>
                                </ListItem>
                                <Divider variant="middle" component="li" />

                                <ListItem>
                                    <ListItemButton
                                        onClick={() =>
                                            navigate(DRAFT_EB_DEVICES)
                                        }
                                    >
                                        <ListItemIcon>
                                            <ElectricBoilerIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primaryTypographyProps={{
                                                variant: "h6",
                                            }}
                                            primary={
                                                <strong>
                                                    {data.totEBDrafts}
                                                </strong>
                                            }
                                            secondary="Draft EB Applications"
                                        />
                                    </ListItemButton>
                                </ListItem>
                                <Divider variant="middle" component="li" />

                                <ListItem>
                                    <ListItemButton
                                        onClick={() =>
                                            navigate(MANU_DRAFT_DEVICES)
                                        }
                                    >
                                        <ListItemIcon>
                                            <BoltIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primaryTypographyProps={{
                                                variant: "h6",
                                            }}
                                            primary={
                                                <strong>
                                                    {data.totTTRDrafts}
                                                </strong>
                                            }
                                            secondary="Draft TTR Applications"
                                        />
                                    </ListItemButton>
                                </ListItem>
                                <Divider variant="middle" component="li" />
                            </List>
                        </AppCardContent>
                    </AppCard>
                </AppPaper>
            </Stack>
            <ReleaseSnackbar open={open} setOpen={setOpen} />
        </Stack>
    )
}

export default ManufacturerHomepage
